import React from 'react';
import { DimensionValue, Platform, StyleSheet } from 'react-native';

import TabActivityCardDefinitionAudio from './tab-activity-card-definition-audio';
import TabActivityCardImage from './tab-activity-card-image';
import TabActivityCardTitleAudio from './tab-activity-card-title-audio';
import { useActivityContext } from '../../../../hooks/use-activity.context';
import useAudioContext from '../../../../hooks/use-audio-context';
import { useSound } from '../../../../hooks/use-sound';
import type { TabActivityStepInterface } from '../../../../interfaces/tab-activity';
import { Card } from '../../card/card';

interface TabActivityCardProps {
  item: TabActivityStepInterface;
  index: number;
  cardWidth: number;
  cardHeight: number;
  active: boolean;
}

const TabActivityCard: React.FC<TabActivityCardProps> = ({
  item,
  index,
  cardWidth,
  cardHeight,
  active,
}) => {
  const cardHeightStyle = {
    height: (Platform.OS === 'web' && !active
      ? 'auto'
      : cardHeight) as DimensionValue,
  };
  const [hasAudioError, setAudioError] = React.useState(false);
  const [wordReproduced, setWordReproduced] = React.useState(false);
  const { audioContext } = useAudioContext();
  const { completeStep } = useActivityContext();

  React.useEffect(() => {
    if (wordReproduced && active) {
      completeStep(index);
    }
  }, [wordReproduced, active, index]);

  const wordSound = useSound({ url: item.wordAudio });
  const {
    isLoaded: isWordSoundLoaded,
    play: playWordSound,
    stop: stopWordSound,
    duration: wordDuration,
  } = wordSound;
  const wordStatus = wordSound.status as any;

  const definitionSound = useSound({ url: item.definitionAudio });
  const {
    isLoaded: isDefinitionSoundLoaded,
    play: playDefinitionSound,
    stop: stopDefinitionSound,
    duration: definitionDuration,
  } = definitionSound;

  React.useEffect(() => {
    if ((wordStatus.isLoaded && wordStatus.isPlaying) || !active) {
      setAudioError(false);
    }
  }, [wordStatus.isLoaded, wordStatus.isPlaying, active]);

  React.useEffect(() => {
    function playDefinition() {
      if (!wordDuration || !definitionDuration) {
        return;
      }
      definitionTimeout = setTimeout(async () => {
        await playDefinitionSound();
        setTimeout(() => setWordReproduced(true), definitionDuration + 100);
      }, wordDuration + 500);
    }

    function playWord() {
      wordTimeout = setTimeout(async () => {
        await playWordSound();
        if (audioContext && audioContext.state !== 'running') {
          setAudioError(true);
        } else {
          if (!item.definitionAudio) {
            setWordReproduced(true);
          } else {
            if (isDefinitionSoundLoaded) {
              playDefinition();
            }
          }
        }
      }, 1000);
    }

    let wordTimeout: NodeJS.Timeout;
    let definitionTimeout: NodeJS.Timeout;

    if (!wordReproduced && active && isWordSoundLoaded) {
      playWord();
    }

    return () => {
      stopWordSound();
      clearTimeout(wordTimeout);
      stopDefinitionSound();
      clearTimeout(definitionTimeout);
    };
  }, [
    active,
    definitionDuration,
    isDefinitionSoundLoaded,
    isWordSoundLoaded,
    item.definitionAudio,
    playDefinitionSound,
    playWordSound,
    stopDefinitionSound,
    stopWordSound,
    wordDuration,
    wordReproduced,
    audioContext,
  ]);

  return (
    <Card
      containerStyle={[
        styles.card,
        active && styles.focusedCard,
        cardHeightStyle,
      ]}
      disabled={!active}
      solid
    >
      {item.image && (
        <TabActivityCardImage item={item} cardWidth={cardWidth - 2} />
      )}

      {item.wordAudio && (
        <TabActivityCardTitleAudio
          item={item}
          active={active}
          cardWidth={cardWidth - 2}
          sound={wordSound}
          showTooltipError={
            index === 0 && Platform.OS === 'web' && hasAudioError
          }
        />
      )}

      {item.definitionAudio && (
        <TabActivityCardDefinitionAudio
          item={item}
          cardWidth={cardWidth - 2}
          active={active}
          sound={definitionSound}
        />
      )}
    </Card>
  );
};

export default TabActivityCard;

const styles = StyleSheet.create({
  card: {
    marginBottom: Platform.OS === 'web' ? 10 : 0,
  },
  focusedCard: {
    borderBottomWidth: 3,
  },
});
